import React, { useEffect, useRef, useState } from 'react'
import CloseSVG from '../../../../assets/svg/close.svg'
import helpBg from '../../../../assets/svg/helpCenterBg.svg'
import Modal from '../../modal'
import { useSubmitTrainingResult } from '../../../../redux/employee/hook/training'
import { FaPlay } from 'react-icons/fa'
import TestScore from './TestScore'

function TrainedEmployee({ isOpen, closeModal, trainedEmployeeData }) {
  const { mutateAsync: submitTrainingResult, isLoading } =
    useSubmitTrainingResult()
  const [shortAnswer, setShortAnswer] = useState('')
  const [testResponse, setTestResponse] = useState()

  const submitForm = async () => {
    const payload = {
      id: trainedEmployeeData.id,
      data: {
        state: answers.length > 0 ? 1 : 0,
        answers: answers,
      },
    }

    await submitTrainingResult(payload).then((response) => {
      setTestResponse(response)
      setTestScore(true)
    })
  }

  const [testScore, setTestScore] = useState(false)
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
      setIsPlaying(true)
    }
  }

  const [step, setStep] = useState('intro')
  const [currentQIndex, setCurrentQIndex] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [timer, setTimer] = useState(
    parseInt(trainedEmployeeData?.timer) * 60 || 480
  )
  const [answers, setAnswers] = useState([])

  const currentQuestion = trainedEmployeeData?.questions[currentQIndex]

  useEffect(() => {
    let interval

    if (step === 'test') {
      if (timer > 0) {
        interval = setTimeout(() => setTimer((prev) => prev - 1), 1000)
      } else {
        // Only submit if not already submitted
        if (!testScore) {
          const handleTimeout = async () => {
            try {
              await submitForm()
              setTestScore(true)
            } catch (error) {
              console.error('Automatic submission failed:', error)
            }
          }
          handleTimeout()
        }
      }
    }

    return () => clearTimeout(interval)
  }, [timer, step, testScore])

  const handleStart = () => {
    setStep('test')
  }

  const canProceed = () => {
    if (currentQuestion.type === 'short_answer') {
      return shortAnswer.trim() !== ''
    }
    return selectedIndex !== null
  }

  const handleNext = () => {
    let answerValue

    if (currentQuestion.type === 'multiple_choice') {
      answerValue =
        currentQuestion.options[selectedIndex]?.option_key.toLowerCase()
    } else if (currentQuestion.type === 'true_false') {
      answerValue = selectedIndex === 0 ? 'true' : 'false'
    } else {
      answerValue = shortAnswer
    }

    const newAnswer = {
      question_id: currentQuestion.id.toString(),
      answer: answerValue,
    }

    const updatedAnswers = [...answers, newAnswer]

    if (currentQIndex < trainedEmployeeData?.questions?.length - 1) {
      setAnswers(updatedAnswers)
      setCurrentQIndex((prev) => prev + 1)
      setSelectedIndex(null)
      setShortAnswer('')
    } else {
      const payload = {
        id: trainedEmployeeData.id,
        data: {
          state: 1,
          answers: updatedAnswers,
        },
      }

      submitTrainingResult(payload).then((response) => {
        setTestResponse(response)
        setTestScore(true)
      })
    }
  }

  const getFileCategory = (url) => {
    if (!url) return 'unknown'

    const ext = url.split('.').pop()?.toLowerCase()

    const videoExts = ['mp4', 'mov', 'webm', 'avi']
    const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt']

    if (videoExts.includes(ext)) return 'video'
    if (docExts.includes(ext)) return 'document'

    return 'unknown'
  }

  const fileCategory = getFileCategory(trainedEmployeeData?.module_doc)

  return (
    <Modal
      scrollable={true}
      className='!w-[600px]'
      isOpen={isOpen}
      centered={true}
    >
      {testScore && (
        <TestScore
          isOpen={testScore}
          closeModal={() => {
            setTestScore(false)
            closeModal()
          }}
          testResponse={testResponse}
        />
      )}
      <div className='flex flex-col overflow-auto w-full'>
        <div className='px-4 pt-3'>
          <div className='flex flex-row justify-between items-center'>
            <span className='header-4'>Employee Test</span>
            <button
              disabled={step === 'test'}
              className={`${
                step === 'test'
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              onClick={() => {
                if (step !== 'test') closeModal()
              }}
            >
              <img src={CloseSVG} alt='close' />
            </button>
          </div>
        </div>
        <hr className='divider mb-2' />
        <main className='mx-4 mb-3'>
          {step === 'intro' ? (
            <>
              <div className='aspect-video w-full rounded overflow-hidden relative'>
                {fileCategory === 'video' && (
                  <>
                    {!isPlaying && (
                      <>
                        <img
                          src={helpBg}
                          alt='Video placeholder'
                          className='w-full h-full object-cover absolute top-0 left-0'
                        />
                        <div
                          onClick={handlePlay}
                          className='absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full h-16 w-16 flex items-center justify-center cursor-pointer'
                        >
                          <FaPlay size={24} className='text-green-700' />
                        </div>
                      </>
                    )}
                    <video
                      ref={videoRef}
                      onPlay={() => setIsPlaying(true)}
                      onPause={() => setIsPlaying(false)}
                      className='w-full h-full object-cover relative z-0'
                      controls={isPlaying}
                    >
                      <source
                        src={trainedEmployeeData.module_doc}
                        type='video/mp4'
                      />
                    </video>
                  </>
                )}

                {fileCategory === 'document' && (
                  <div className='w-full h-full overflow-auto'>
                    <iframe
                      src={trainedEmployeeData.module_doc}
                      title='PDF Viewer'
                      className='w-full h-[600px] border rounded'
                    />
                  </div>
                )}
              </div>
              <h2 className='text-xl font-semibold mt-3'>
                {trainedEmployeeData.module_name}
              </h2>
              <p className='text-sm text-gray-600'>
                {trainedEmployeeData.module_description}
              </p>
              <p className='text-red-600 text-xs mt-2'>
                {`You have ${trainedEmployeeData?.timer} minutes to take the test. `}
                {parseInt(trainedEmployeeData?.retry_limit) > 0
                  ? `You can retake it ${
                      trainedEmployeeData?.retry_limit
                    } time${
                      trainedEmployeeData?.retry_limit !== '1' ? 's' : ''
                    } if you fail.`
                  : 'You cannot retake this test if you fail.'}
              </p>
              <button
                onClick={handleStart}
                className='bg-hr-primary-1 mt-3 w-[130px] flex items-center justify-center ml-auto text-white px-6 py-2 rounded hover:bg-green-700'
              >
                Start Test
              </button>
            </>
          ) : (
            <>
              <div className='aspect-video w-full rounded overflow-hidden relative'>
                {fileCategory === 'video' && (
                  <div className='aspect-video w-full bg-gray-200 rounded overflow-hidden'>
                    <video controls className='w-full object-cover'>
                      <source
                        src={trainedEmployeeData.module_doc}
                        type='video/mp4'
                      />
                    </video>
                  </div>
                )}
                {fileCategory === 'document' && (
                  <div className='w-full h-full overflow-auto'>
                    <iframe
                      src={trainedEmployeeData.module_doc}
                      title='PDF Viewer'
                      className='w-full h-full border rounded'
                    />
                  </div>
                )}
              </div>
              <div className='flex justify-between items-center mt-3 text-sm text-gray-600'>
                <h2 className='text-xl font-semibold'>
                  {trainedEmployeeData.module_name}
                </h2>
                <p className='text-red-600 text-sm font-semibold'>
                  {String(Math.floor(timer / 60)).padStart(2, '0')} :{' '}
                  {String(timer % 60).padStart(2, '0')}
                </p>
              </div>

              <div className='mt-4'>
                <p className='font-bold'>Question {currentQIndex + 1}</p>
                <p className='mt-2'>{currentQuestion.question}</p>

                <div className='mt-4 space-y-2'>
                  {currentQuestion?.type === 'multiple_choice' &&
                    currentQuestion?.options?.map((opt, id) => (
                      <label
                        key={id}
                        className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                          selectedIndex === id
                            ? 'bg-green-100 border-green-600'
                            : ''
                        }`}
                      >
                        <input
                          type='radio'
                          name={`question-${currentQuestion.id}`}
                          checked={selectedIndex === id}
                          onChange={() => setSelectedIndex(id)}
                          className='accent-green-600'
                        />
                        <span className='text-xs font-normal'>{`(${opt.option_key})`}</span>
                        <span className='text-sm font-medium'>
                          {opt.option_text}
                        </span>
                      </label>
                    ))}

                  {currentQuestion?.type === 'true_false' && (
                    <div className='space-y-2'>
                      {['True', 'False'].map((option, id) => (
                        <label
                          key={id}
                          className={`flex items-center space-x-2 p-2 border rounded cursor-pointer ${
                            selectedIndex === id
                              ? 'bg-green-100 border-green-600'
                              : ''
                          }`}
                        >
                          <input
                            type='radio'
                            name={`question-${currentQuestion.id}`}
                            checked={selectedIndex === id}
                            onChange={() => setSelectedIndex(id)}
                            className='accent-green-600'
                          />
                          <span className='text-sm font-medium'>{option}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {currentQuestion?.type === 'short_answer' && (
                    <div className='mt-2'>
                      <input
                        type='text'
                        value={shortAnswer}
                        onChange={(e) => setShortAnswer(e.target.value)}
                        className='w-full p-2 border rounded focus:outline-none focus:ring-1 focus:ring-green-600'
                        placeholder='Type your answer here...'
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className='flex justify-between mt-6'>
                <button
                  className={`px-4 py-2 border rounded ${
                    currentQIndex === 0
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                  disabled={currentQIndex === 0}
                  onClick={() => {
                    setCurrentQIndex((prev) => prev - 1)
                    setSelectedIndex(
                      answers[currentQIndex - 1]?.selected ?? null
                    )
                    setAnswers((prev) => prev.slice(0, -1))
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={handleNext}
                  className={`px-6 py-2 rounded ${
                    canProceed()
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  }`}
                  disabled={!canProceed()}
                >
                  {currentQIndex === trainedEmployeeData?.questions?.length - 1
                    ? 'Submit'
                    : 'Next'}
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </Modal>
  )
}

export default TrainedEmployee
